import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, AlertTitle, Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormCheckbox, FormRadioGroup, FormSwitch, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField, NumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type HousingPropertyGoalFormType, getHousingPropertyFormSchema } from "Forms/Schemas/Goals/housingPropertyFormSchema";
import { minDate } from "Forms/Schemas/Goals/housingPropertyFormSchema";
import { toRadioOptions } from "Forms/fieldOptions";
import {
  getGetAnalysisGoalsQueryKey,
  getGetHousingPropertyGoalQueryKey,
  useCreateHousingPropertyGoal,
  useGetAnalysisClients,
  useUpdateHousingPropertyGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  AnalysisClientItemRelationType,
  GoalOverviewSubType,
  type HousingPropertyGoalDto,
  HousingPropertyGoalDtoPriorityLevel,
  TimeRangeType,
} from "Infrastructure/ApiZfp/Schema";
import {
  DEFAULT_FEE_RATE,
  DEFAULT_INFLATION_RATE,
  DEFAULT_LOAN_DURATION,
  DEFAULT_LTV,
  INTEREST_RATE_CONSERVATIVE,
  INTEREST_RATE_MORTGAGE,
} from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInYears, format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useHousingPropertyCalculations } from "../Hooks/useHousingPropertyCalculations";
import { ModelingAccordion } from "./ModelingAccordion";
import { MortgageComparisonTable } from "./MortgageComparisonTable";
import { PropertyPurchaseChart } from "./PropertyPurchaseChart";

type Props = {
  readonly onClose: () => void;
  readonly onSubTypeChange: (goalSubType: GoalOverviewSubType) => void;
  readonly editGoal?: HousingPropertyGoalDto;
  readonly goalId?: string;
};

export const HousingPropertyGoalForm = forwardRef<{}, Props>(({ onSubTypeChange, onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const { data: clientRelationsData } = useGetAnalysisClients(analysisId, versionId);
  const client = clientRelationsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client);
  const birthDate = client?.birthDate;

  const methods = useForm<HousingPropertyGoalFormType>({
    resolver: yupResolver(getHousingPropertyFormSchema(t)),
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          name: editGoal.name,
          includeInflation: !!editGoal.modeling.inflation?.enabled,
          includeEntryFee: !!editGoal.modeling.fee?.enabled,
          inflationRate: editGoal.modeling.inflation?.rate ?? 0,
          feeRate: editGoal.modeling.fee?.rate ?? 0,
          interestRate: editGoal.modeling.appreciationRate?.toString() as any,
          goalSubType: GoalOverviewSubType.housing_property,
          currentSavingsAmount: editGoal.data.currentSavings?.value.toString() as any,
          downpaymentAmount: editGoal.data.targetSavings?.value.toString() as any,
          expectedPriceAmount: editGoal.data.expectedPurchaseAmount?.value.toString() as any,
          loanAmount: editGoal.data.loanAmount?.value.toString() as any,
          loanDuration: editGoal.modeling.mortgageTerm?.value.toString() as any,
          loanInterestRate: editGoal.modeling.interestRate?.toString() as any,
          loanToValue: editGoal.data.loanToValueRatio?.toString() as any,
          targetDate: (editGoal.data.targetDate && new Date(editGoal.data.targetDate)) || new Date(),
          priority: editGoal.priorityLevel === HousingPropertyGoalDtoPriorityLevel.high,
          investCurrentSavings: !!editGoal.modeling.investCurrentSavings,
          note: editGoal.note || "",
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        }
      : {
          includeInflation: true,
          includeEntryFee: true,
          investCurrentSavings: true,
          inflationRate: DEFAULT_INFLATION_RATE.toString() as any,
          interestRate: INTEREST_RATE_CONSERVATIVE.toString() as any,
          feeRate: DEFAULT_INFLATION_RATE.toString() as any,
          goalSubType: GoalOverviewSubType.housing_property,
          loanDuration: DEFAULT_LOAN_DURATION,
          loanInterestRate: INTEREST_RATE_MORTGAGE.toString() as any,
          loanToValue: DEFAULT_LTV.toString() as any,
          name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingProperty),
          referenceStartDate: new Date(),
        },
  });

  const { control, setValue, watch, clearErrors, handleSubmit } = methods;

  const queryClient = useQueryClient();

  const { mutate: createHousingPropertyGoal } = useCreateHousingPropertyGoal();

  const { mutate: updateHousingPropertyGoal } = useUpdateHousingPropertyGoal();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEditGoal, (data) => console.log(data))();
      } else {
        handleSubmit(onCreateGoal)();
      }
    },
  }));

  const currentSavingsAmount = watch("currentSavingsAmount");
  const downpaymentAmount = watch("downpaymentAmount");
  const expectedPriceAmount = watch("expectedPriceAmount");
  const includeEntryFee = watch("includeEntryFee");
  const includeInflation = watch("includeInflation");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const loanAmount = watch("loanAmount");
  const loanDuration = watch("loanDuration");
  const loanInterestRate = watch("loanInterestRate");
  const loanToValue = watch("loanToValue");
  const targetDate = watch("targetDate");
  const investCurrentSavings = watch("investCurrentSavings");
  const referenceStartDate = watch("referenceStartDate");

  const clientAge = differenceInYears(referenceStartDate, birthDate ? new Date(birthDate) : referenceStartDate);
  const isInvalidTargetDate = isBefore(startOfDay(targetDate), startOfDay(minDate));
  const {
    calculations: {
      hasEnoughMoney,
      overpayment,
      toSaveUp,
      loanTotalAmount,
      interestPaid,
      loanPaymentAmount,
      monthlyContribution,
      monthlyContributionWithFee,
    },
    handlers: { handleExpectedPriceChange, handleDownpaymentChange, handleLoanAmountChange, handleLtvChange },
  } = useHousingPropertyCalculations({ watch, setValue });

  const shouldRenderChart =
    currentSavingsAmount >= 0 &&
    downpaymentAmount >= 0 &&
    expectedPriceAmount > 0 &&
    loanAmount > 0 &&
    loanDuration > 0 &&
    loanInterestRate >= 0 &&
    loanToValue > 0 &&
    monthlyContribution &&
    loanPaymentAmount &&
    targetDate &&
    !isInvalidTargetDate &&
    !hasEnoughMoney &&
    clientAge < 71;

  useEffect(() => {
    if (!targetDate || isInvalidTargetDate) return;
    clearErrors("targetDate");
  }, [targetDate, isInvalidTargetDate, clearErrors]);

  const onCreateGoal = (data: HousingPropertyGoalFormType) => {
    createHousingPropertyGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingPropertyGoalDtoPriorityLevel.high : HousingPropertyGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            currentSavings: { value: data.currentSavingsAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            targetSavings: { value: data.downpaymentAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            expectedPurchaseAmount: { value: data.expectedPriceAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            loanAmount: { value: data.loanAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            remainingSavings: { value: toSaveUp, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            loanToValueRatio: data.loanToValue,
          },
          modeling: {
            interestRate: data.loanInterestRate,
            investmentAmount: { value: monthlyContributionWithFee, currencyCode: "CZK", frequency: AmountFrequency.monthly },
            mortgageTerm: { value: data.loanDuration, type: TimeRangeType.year },
            loanPayment: { value: loanPaymentAmount, currencyCode: "CZK", frequency: AmountFrequency.monthly },
            investCurrentSavings: true,
            totalAmount: { value: loanTotalAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            inflation: { enabled: !!includeInflation, rate: data.inflationRate },
            fee: { enabled: !!includeEntryFee, rate: data.feeRate },
            overpaymentRate: overpayment,
            totalInterestPaid: interestPaid,
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: HousingPropertyGoalFormType) => {
    updateHousingPropertyGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingPropertyGoalDtoPriorityLevel.high : HousingPropertyGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            currentSavings: { value: data.currentSavingsAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            expectedPurchaseAmount: { value: data.expectedPriceAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            targetSavings: { value: data.downpaymentAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            loanAmount: { value: data.loanAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            remainingSavings: { value: toSaveUp, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            loanToValueRatio: data.loanToValue,
          },
          modeling: {
            interestRate: data.loanInterestRate,
            mortgageTerm: { value: data.loanDuration, type: TimeRangeType.year },
            loanPayment: { value: loanPaymentAmount, currencyCode: "CZK", frequency: AmountFrequency.monthly },
            appreciationRate: data.interestRate,
            investCurrentSavings: data.investCurrentSavings,
            investmentAmount: { value: monthlyContributionWithFee, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            totalAmount: { value: loanTotalAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            inflation: { enabled: !!includeInflation, rate: data.inflationRate },
            fee: { enabled: !!includeEntryFee, rate: data.feeRate },
            overpaymentRate: overpayment,
            totalInterestPaid: interestPaid,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetHousingPropertyGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const renderHint = () => {
    if (hasEnoughMoney) {
      return (
        <Alert severity="info">
          <AlertTitle>{t(Resources.AnalysisDetail.Goals.HasEnoughMoneyHint.Title)}</AlertTitle>
          {t(Resources.AnalysisDetail.Goals.HasEnoughMoneyHint.Content)}
        </Alert>
      );
    }

    if (clientAge >= 60 && clientAge < 71) {
      return (
        <Alert severity="info">
          <AlertTitle>{t(Resources.AnalysisDetail.Goals.QuiteOldHint.Title)}</AlertTitle>
          <Typography variant="body2">
            {t(Resources.AnalysisDetail.Goals.QuiteOldHint.FirstPhrase)}{" "}
            <Typography variant="body2" display="inline" fontWeight={500}>
              60 {t(Resources.Common.Years)}
            </Typography>{" "}
            {t(Resources.AnalysisDetail.Goals.QuiteOldHint.SecondPhrase)}
          </Typography>
        </Alert>
      );
    }

    if (clientAge >= 71) {
      return (
        <Alert severity="info">
          <AlertTitle>{t(Resources.AnalysisDetail.Goals.TooOldHint.Title)}</AlertTitle>
          <Typography variant="body2">
            {t(Resources.AnalysisDetail.Goals.TooOldHint.FirstPhrase)}{" "}
            <Typography variant="body2" display="inline" fontWeight={500}>
              71 {t(Resources.Common.Years)}
            </Typography>{" "}
            {t(Resources.AnalysisDetail.Goals.TooOldHint.SecondPhrase)}
          </Typography>
        </Alert>
      );
    }
  };

  const renderChart = () => {
    if (shouldRenderChart) {
      return (
        <Grid size={12}>
          <Box padding={Spacing.S} display="flex" justifyContent="space-around" alignItems="center">
            <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="caption" fontWeight={500}>
                {t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
              </Typography>
              <Typography variant="caption">
                {formatCurrency(Math.round(Number(monthlyContributionWithFee)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
              </Typography>
            </Stack>
            <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="caption" fontWeight={500}>
                {t(Resources.AnalysisDetail.Goals.LoanPayment)}
              </Typography>
              <Typography variant="caption">
                {formatCurrency(Math.round(Number(loanPaymentAmount)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
              </Typography>
            </Stack>
          </Box>
          <PropertyPurchaseChart
            investCurrentSavings={investCurrentSavings}
            currentSavingsAmount={Number(currentSavingsAmount)}
            downpaymentAmount={Number(downpaymentAmount)}
            inflationRate={Number(inflationRate)}
            interestRate={interestRate ? Number(interestRate) : 0}
            loanAmount={Number(loanAmount)}
            loanDuration={Number(loanDuration)}
            monthlyContributionAmount={Number(monthlyContribution)}
            targetDate={targetDate}
            loanInterestRate={Number(loanInterestRate)}
            referenceStartDate={referenceStartDate}
          />
        </Grid>
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Housing)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormCheckbox
                  control={control}
                  name="priority"
                  label={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  required
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <FormRadioGroup
                  control={control}
                  name="goalSubType"
                  label={t(Resources.AnalysisDetail.Goals.WhatAreYouPlanning)}
                  options={toRadioOptions([
                    {
                      code: GoalOverviewSubType.housing_property,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingProperty),
                    },
                    {
                      code: GoalOverviewSubType.housing_mortgage,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingMortgage),
                    },
                    {
                      code: GoalOverviewSubType.housing_other,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingOther),
                    },
                  ])}
                  defaultValue={GoalOverviewSubType.housing_property}
                  columns={2}
                  disabled={!!editGoal || readOnly}
                  onChange={(goalSubType) => onSubTypeChange(goalSubType as GoalOverviewSubType)}
                />
              </Grid>
              <Grid size={6}>
                <FormDatePicker
                  control={control}
                  name="targetDate"
                  label={t(Resources.AnalysisDetail.Goals.PurchaseDate)}
                  required
                  minDate={startOfMonth(addMonths(new Date(), 1))}
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="currentSavingsAmount"
                  label={t(Resources.AnalysisDetail.Goals.CurrentSavingsAmount)}
                  required
                  scale={0}
                  min={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={5}>
                <FormNumberField
                  control={control}
                  name="expectedPriceAmount"
                  label={t(Resources.AnalysisDetail.Goals.ExpectedPriceAmount)}
                  required
                  scale={0}
                  min={100}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onChange={handleExpectedPriceChange}
                  fullWidth
                />
              </Grid>
              <Grid size={2}>
                <FormNumberField
                  control={control}
                  name="loanToValue"
                  label={t(Resources.Common.LTV)}
                  required
                  scale={2}
                  min={0}
                  thousandsSeparator=" "
                  unit="%"
                  disabled={readOnly}
                  onChange={handleLtvChange}
                  fullWidth
                />
              </Grid>
              <Grid size={5}>
                <FormNumberField
                  control={control}
                  name="downpaymentAmount"
                  label={t(Resources.AnalysisDetail.Goals.Downpayment)}
                  required
                  scale={0}
                  min={0}
                  max={expectedPriceAmount}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onChange={handleDownpaymentChange}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="loanAmount"
                  label={t(Resources.AnalysisDetail.Goals.LoanAmount)}
                  required
                  scale={2}
                  min={0}
                  max={expectedPriceAmount}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onChange={handleLoanAmountChange}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <NumberField
                  label={t(Resources.AnalysisDetail.Goals.NeededToSaveUp)}
                  value={toSaveUp}
                  disabled
                  scale={2}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  fullWidth
                />
              </Grid>
              {renderHint()}
            </Grid>
          </Box>
          {targetDate &&
            currentSavingsAmount >= 0 &&
            expectedPriceAmount > 0 &&
            loanToValue > 0 &&
            downpaymentAmount >= 0 &&
            loanAmount > 0 &&
            !hasEnoughMoney &&
            clientAge < 71 && (
              <Box>
                <ModelingAccordion>
                  <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                    <Grid size={5}>
                      <FormNumberField
                        control={control}
                        name="loanInterestRate"
                        label={t(Resources.AnalysisDetail.Goals.LoanInterestRate)}
                        required
                        scale={2}
                        min={0}
                        thousandsSeparator=""
                        unit="%"
                        disabled={readOnly}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={2}>
                      <FormNumberField
                        control={control}
                        name="loanDuration"
                        label={t(Resources.AnalysisDetail.Goals.LoanDuration)}
                        required
                        scale={0}
                        thousandsSeparator=" "
                        unit={t(Resources.Common.Years)}
                        disabled={readOnly}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={5}>
                      <NumberField
                        label={t(Resources.AnalysisDetail.Goals.LoanPayment)}
                        value={loanPaymentAmount}
                        disabled
                        scale={2}
                        thousandsSeparator=" "
                        unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <NumberField
                        label={t(Resources.AnalysisDetail.Goals.TotalPaid)}
                        value={loanTotalAmount}
                        disabled
                        scale={2}
                        thousandsSeparator=" "
                        unit={t(Resources.Common.CZKSuffix)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <FormNumberField
                        control={control}
                        name="interestRate"
                        label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                        required
                        scale={1}
                        min={0}
                        thousandsSeparator=" "
                        unit="%"
                        disabled={readOnly}
                        onChange={(newInterestRate) => {
                          if (
                            (includeInflation && Number(newInterestRate) === Number(inflationRate)) ||
                            (!includeInflation && Number(newInterestRate) <= 0)
                          ) {
                            setValue("interestRate", interestRate);
                            return;
                          }
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={12} display="flex" alignItems="center">
                      <FormSwitch
                        control={control}
                        name="investCurrentSavings"
                        label={t(Resources.AnalysisDetail.Goals.InvestCurrentSavings)}
                        disabled={readOnly}
                      />
                    </Grid>
                    <Grid size={9} display="flex" alignItems="center">
                      <FormSwitch
                        control={control}
                        name="includeInflation"
                        label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                        disabled={Number(interestRate) === Number(inflationRate) || readOnly}
                        onChange={(checked) => {
                          const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;
                          setValue("inflationRate", newInflationRate);
                        }}
                      />
                    </Grid>
                    <Grid size={3}>
                      <FormNumberField
                        control={control}
                        name="inflationRate"
                        scale={1}
                        min={0}
                        thousandsSeparator=""
                        radix=","
                        mapToRadix={["."]}
                        unit="%"
                        disabled={!includeInflation || readOnly}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={9} display="flex" alignItems="center">
                      <FormSwitch
                        control={control}
                        name="includeEntryFee"
                        label={t(Resources.AnalysisDetail.Goals.IncludeEntryFee)}
                        disabled={readOnly}
                        onChange={(checked) => {
                          const newFeeRate = checked ? DEFAULT_FEE_RATE : 0;

                          setValue("feeRate", newFeeRate);
                        }}
                      />
                    </Grid>
                    <Grid size={3}>
                      <FormNumberField
                        control={control}
                        name="feeRate"
                        scale={1}
                        min={0}
                        max={20}
                        thousandsSeparator=" "
                        unit="%"
                        disabled={!includeEntryFee || readOnly}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </ModelingAccordion>
              </Box>
            )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              {renderChart()}
              {loanDuration > 0 && loanPaymentAmount > 0 && loanAmount > 0 && loanInterestRate > 0 && (
                <Grid size={12}>
                  <Typography my={Spacing.M} variant="subtitle1" fontWeight={700}>
                    {t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.Comparison)}
                  </Typography>
                  <MortgageComparisonTable
                    loanInterestRate={loanInterestRate}
                    loanAmount={loanAmount}
                    originalLoanDuration={loanDuration}
                    loanPaymentAmount={loanPaymentAmount}
                  />
                </Grid>
              )}
              {loanDuration > 0 && loanPaymentAmount > 0 && (
                <>
                  <Grid size={6}>
                    <NumberField
                      name="overpayment"
                      label={t(Resources.AnalysisDetail.Goals.Overpayment)}
                      value={overpayment}
                      disabled
                      scale={2}
                      thousandsSeparator=" "
                      unit="%"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <NumberField
                      label={t(Resources.AnalysisDetail.Goals.InterestPaid)}
                      value={interestPaid}
                      disabled
                      scale={2}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              <Grid size={12}>
                <Divider sx={{ marginTop: Spacing.M, marginBottom: Spacing.M }} />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
